import { useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import {
  ArrowLeft,
  Check,
  Trash2,
  ExternalLink,
  ChevronDown,
  ChevronRight,
  Plus,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  getAdminSeriesDetail,
  updateAdminFilm,
  updateAdminEpisode,
  createAdminEpisode,
  deleteAdminEpisode,
  deleteAdminFilm,
  approveShow,
  rejectShow,
  type AdminSeriesDetail,
  type AdminEpisodeDetail,
} from '@/lib/api/admin'

const emptyEpisode: Partial<AdminEpisodeDetail> = {
  title: '',
  description: '',
  videoUrl: '',
  thumbnailUrl: '',
  duration: 0,
}

export function AdminShowDetail() {
  const { seriesId } = useParams<{ seriesId: string }>()
  const navigate = useNavigate()
  const [series, setSeries] = useState<AdminSeriesDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [banner, setBanner] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [filmForm, setFilmForm] = useState<Record<string, string | number | boolean | null>>({})
  const [expandedEpisodeId, setExpandedEpisodeId] = useState<string | null>(null)
  const [episodeForms, setEpisodeForms] = useState<Record<string, Partial<AdminEpisodeDetail>>>({})
  const [addingEpisode, setAddingEpisode] = useState(false)
  const [newEpisode, setNewEpisode] = useState(emptyEpisode)
  const [confirmDelete, setConfirmDelete] = useState<'film' | string | null>(null)

  const load = () => {
    if (!seriesId) return
    setLoading(true)
    setError(null)
    getAdminSeriesDetail(seriesId)
      .then((data) => {
        setSeries(data)
        setFilmForm({
          title: data.title ?? '',
          synopsis: data.synopsis ?? '',
          genre: data.genre ?? '',
          language: data.language ?? 'English',
          releaseYear: data.releaseYear ?? new Date().getFullYear(),
          coverImage: data.coverImage ?? '',
          trailerUrl: data.trailerUrl ?? '',
          monetization: data.monetization ?? 'free',
          price: data.price ?? null,
          isFeatured: data.isFeatured ?? false,
          isNew: data.isNew ?? true,
        })
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Failed to load show')
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    load()
  }, [seriesId])

  const showBanner = (msg: string) => {
    setBanner(msg)
    setTimeout(() => setBanner(null), 4000)
  }

  const handleSaveFilm = async () => {
    if (!series?.filmId || saving) return
    setSaving(true)
    try {
      const updated = await updateAdminFilm(series.filmId, {
        title: String(filmForm.title),
        synopsis: String(filmForm.synopsis),
        genre: String(filmForm.genre),
        language: String(filmForm.language),
        releaseYear: Number(filmForm.releaseYear) || new Date().getFullYear(),
        coverImage: String(filmForm.coverImage),
        trailerUrl: filmForm.trailerUrl ? String(filmForm.trailerUrl) : null,
        monetization: String(filmForm.monetization),
        price: filmForm.price != null ? Number(filmForm.price) : null,
        isFeatured: Boolean(filmForm.isFeatured),
        isNew: Boolean(filmForm.isNew),
      })
      setSeries((s) => (s ? { ...s, ...updated } : null))
      showBanner('Series saved.')
    } catch (err) {
      showBanner(err instanceof Error ? err.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  const handleApprove = async () => {
    if (!seriesId || saving) return
    setSaving(true)
    try {
      await approveShow(seriesId)
      showBanner('Series approved.')
      load()
    } catch (err) {
      showBanner(err instanceof Error ? err.message : 'Approve failed')
    } finally {
      setSaving(false)
    }
  }

  const handleReject = async () => {
    if (!seriesId || saving) return
    const reason = window.prompt('Rejection reason (optional):') || undefined
    setSaving(true)
    try {
      await rejectShow(seriesId, reason)
      showBanner('Series rejected.')
      load()
    } catch (err) {
      showBanner(err instanceof Error ? err.message : 'Reject failed')
    } finally {
      setSaving(false)
    }
  }

  const handleSaveEpisode = async (episodeId: string) => {
    const form = episodeForms[episodeId]
    if (!form || saving) return
    setSaving(true)
    try {
      await updateAdminEpisode(episodeId, {
        title: form.title,
        description: form.description,
        episodeNumber: form.episodeNumber,
        season: form.season ?? 1,
        videoUrl: form.videoUrl,
        thumbnailUrl: form.thumbnailUrl,
        duration: form.duration,
        isFree: form.isFree,
      })
      showBanner('Episode saved.')
      setExpandedEpisodeId(null)
      load()
    } catch (err) {
      showBanner(err instanceof Error ? err.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  const openEpisodeForm = (ep: AdminEpisodeDetail) => {
    setEpisodeForms((prev) => ({
      ...prev,
      [ep.episodeId]: {
        title: ep.title,
        description: ep.description ?? '',
        episodeNumber: ep.episodeNumber,
        season: ep.season ?? 1,
        videoUrl: ep.videoUrl ?? '',
        thumbnailUrl: ep.thumbnailUrl ?? '',
        duration: ep.duration ?? 0,
        isFree: ep.isFree ?? true,
      },
    }))
    setExpandedEpisodeId(ep.episodeId)
  }

  const handleAddEpisode = async () => {
    if (!series?.filmId || !newEpisode.title?.trim() || saving) return
    setSaving(true)
    try {
      await createAdminEpisode(series.filmId, {
        title: newEpisode.title.trim(),
        description: newEpisode.description,
        videoUrl: newEpisode.videoUrl,
        thumbnailUrl: newEpisode.thumbnailUrl,
        duration: newEpisode.duration,
      })
      showBanner('Episode added.')
      setAddingEpisode(false)
      setNewEpisode(emptyEpisode)
      load()
    } catch (err) {
      showBanner(err instanceof Error ? err.message : 'Add failed')
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteEpisode = async (episodeId: string) => {
    if (saving) return
    setSaving(true)
    try {
      await deleteAdminEpisode(episodeId)
      showBanner('Episode deleted.')
      setConfirmDelete(null)
      setExpandedEpisodeId(null)
      load()
    } catch (err) {
      showBanner(err instanceof Error ? err.message : 'Delete failed')
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteFilm = async () => {
    if (!series?.filmId || saving) return
    setSaving(true)
    try {
      await deleteAdminFilm(series.filmId)
      showBanner('Series deleted.')
      setConfirmDelete(null)
      navigate('/admin/shows')
    } catch (err) {
      showBanner(err instanceof Error ? err.message : 'Delete failed')
    } finally {
      setSaving(false)
    }
  }

  if (loading && !series) {
    return (
      <div className="space-y-6">
        <p className="text-sm text-gray-400">Loading show…</p>
      </div>
    )
  }

  if (error || !series) {
    return (
      <div className="space-y-6">
        <Link to="/admin/content" className="inline-flex items-center gap-1 text-sm text-gray-400 hover:text-white">
          <ArrowLeft className="h-4 w-4" /> Back
        </Link>
        <p className="text-sm text-red-400">{error ?? 'Show not found.'}</p>
      </div>
    )
  }

  const episodes = series.episodes ?? []

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Link
            to="/admin/content"
            className="inline-flex items-center gap-1 text-sm text-gray-400 hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" /> Back to Content
          </Link>
          <Link
            to="/admin/shows"
            className="inline-flex items-center gap-1 text-sm text-gray-400 hover:text-white"
          >
            Back to All Shows
          </Link>
        </div>
        <div className="flex items-center gap-2">
          <span className="rounded-full bg-white/10 px-2 py-0.5 text-xs capitalize text-gray-300">
            {series.status ?? 'draft'}
          </span>
          {series.status === 'pending_review' && (
            <>
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={handleApprove} disabled={saving}>
                <Check className="h-3.5 w-3.5 mr-1" /> Approve
              </Button>
              <Button size="sm" variant="secondary" className="border-red-800 text-red-300" onClick={handleReject} disabled={saving}>
                Reject
              </Button>
            </>
          )}
          {confirmDelete === 'film' ? (
            <>
              <Button size="sm" variant="secondary" className="border-red-600 text-red-400" onClick={handleDeleteFilm} disabled={saving}>
                Confirm delete series
              </Button>
              <Button size="sm" variant="secondary" onClick={() => setConfirmDelete(null)}>Cancel</Button>
            </>
          ) : (
            <Button size="sm" variant="secondary" className="border-red-800 text-red-400" onClick={() => setConfirmDelete('film')}>
              <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete series
            </Button>
          )}
        </div>
      </div>

      {banner && (
        <div className="rounded-lg border border-emerald-700 bg-emerald-900/30 px-3 py-2 text-sm text-emerald-200">
          {banner}
        </div>
      )}

      {/* Series metadata */}
      <section className="rounded-xl border border-gray-800 bg-black/40 p-6">
        <h2 className="text-lg font-semibold text-white mb-4">Series details</h2>
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Title</label>
            <input
              type="text"
              value={String(filmForm.title ?? '')}
              onChange={(e) => setFilmForm((f) => ({ ...f, title: e.target.value }))}
              className="w-full rounded-lg border border-gray-700 bg-black/50 px-3 py-2 text-sm text-white"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Genre</label>
            <input
              type="text"
              value={String(filmForm.genre ?? '')}
              onChange={(e) => setFilmForm((f) => ({ ...f, genre: e.target.value }))}
              className="w-full rounded-lg border border-gray-700 bg-black/50 px-3 py-2 text-sm text-white"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs font-medium text-gray-500 mb-1">Synopsis / description</label>
            <textarea
              rows={3}
              value={String(filmForm.synopsis ?? '')}
              onChange={(e) => setFilmForm((f) => ({ ...f, synopsis: e.target.value }))}
              className="w-full rounded-lg border border-gray-700 bg-black/50 px-3 py-2 text-sm text-white"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Language</label>
            <input
              type="text"
              value={String(filmForm.language ?? '')}
              onChange={(e) => setFilmForm((f) => ({ ...f, language: e.target.value }))}
              className="w-full rounded-lg border border-gray-700 bg-black/50 px-3 py-2 text-sm text-white"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Release year</label>
            <input
              type="number"
              value={Number(filmForm.releaseYear) || ''}
              onChange={(e) => setFilmForm((f) => ({ ...f, releaseYear: e.target.value ? parseInt(e.target.value, 10) : 0 }))}
              className="w-full rounded-lg border border-gray-700 bg-black/50 px-3 py-2 text-sm text-white"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Cover image URL</label>
            <input
              type="text"
              value={String(filmForm.coverImage ?? '')}
              onChange={(e) => setFilmForm((f) => ({ ...f, coverImage: e.target.value }))}
              className="w-full rounded-lg border border-gray-700 bg-black/50 px-3 py-2 text-sm text-white"
              placeholder="https://..."
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Trailer URL</label>
            <input
              type="text"
              value={String(filmForm.trailerUrl ?? '')}
              onChange={(e) => setFilmForm((f) => ({ ...f, trailerUrl: e.target.value }))}
              className="w-full rounded-lg border border-gray-700 bg-black/50 px-3 py-2 text-sm text-white"
              placeholder="https://..."
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Monetization</label>
            <select
              value={String(filmForm.monetization ?? 'free')}
              onChange={(e) => setFilmForm((f) => ({ ...f, monetization: e.target.value }))}
              className="w-full rounded-lg border border-gray-700 bg-black/50 px-3 py-2 text-sm text-white"
            >
              <option value="free">Free</option>
              <option value="paid">Paid</option>
              <option value="subscription">Subscription</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Price</label>
            <input
              type="number"
              step="0.01"
              value={filmForm.price != null ? filmForm.price : ''}
              onChange={(e) => setFilmForm((f) => ({ ...f, price: e.target.value ? parseFloat(e.target.value) : null }))}
              className="w-full rounded-lg border border-gray-700 bg-black/50 px-3 py-2 text-sm text-white"
            />
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm text-gray-300">
              <input
                type="checkbox"
                checked={Boolean(filmForm.isFeatured)}
                onChange={(e) => setFilmForm((f) => ({ ...f, isFeatured: e.target.checked }))}
                className="rounded border-gray-600"
              />
              Featured
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-300">
              <input
                type="checkbox"
                checked={Boolean(filmForm.isNew)}
                onChange={(e) => setFilmForm((f) => ({ ...f, isNew: e.target.checked }))}
                className="rounded border-gray-600"
              />
              New
            </label>
          </div>
        </div>
        <div className="mt-4">
          <Button onClick={handleSaveFilm} disabled={saving} className="bg-[#D4AF37] text-black hover:bg-[#FFD700]">
            Save series details
          </Button>
        </div>
      </section>

      {/* Episodes */}
      <section className="rounded-xl border border-gray-800 bg-black/40 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white">Episodes ({episodes.length})</h2>
          {!addingEpisode && (
            <Button size="sm" variant="secondary" onClick={() => setAddingEpisode(true)}>
              <Plus className="h-4 w-4 mr-1" /> Add episode
            </Button>
          )}
        </div>

        {addingEpisode && (
          <div className="mb-6 rounded-lg border border-gray-700 bg-black/50 p-4 space-y-3">
            <h3 className="text-sm font-medium text-white">New episode</h3>
            <input
              type="text"
              placeholder="Title *"
              value={newEpisode.title ?? ''}
              onChange={(e) => setNewEpisode((n) => ({ ...n, title: e.target.value }))}
              className="w-full rounded-lg border border-gray-700 bg-black/50 px-3 py-2 text-sm text-white"
            />
            <textarea
              placeholder="Description"
              value={newEpisode.description ?? ''}
              onChange={(e) => setNewEpisode((n) => ({ ...n, description: e.target.value }))}
              className="w-full rounded-lg border border-gray-700 bg-black/50 px-3 py-2 text-sm text-white"
              rows={2}
            />
            <input
              type="text"
              placeholder="Video URL"
              value={newEpisode.videoUrl ?? ''}
              onChange={(e) => setNewEpisode((n) => ({ ...n, videoUrl: e.target.value }))}
              className="w-full rounded-lg border border-gray-700 bg-black/50 px-3 py-2 text-sm text-white"
            />
            <input
              type="text"
              placeholder="Thumbnail URL"
              value={newEpisode.thumbnailUrl ?? ''}
              onChange={(e) => setNewEpisode((n) => ({ ...n, thumbnailUrl: e.target.value }))}
              className="w-full rounded-lg border border-gray-700 bg-black/50 px-3 py-2 text-sm text-white"
            />
            <input
              type="number"
              placeholder="Duration (seconds)"
              value={newEpisode.duration ?? 0}
              onChange={(e) => setNewEpisode((n) => ({ ...n, duration: parseInt(e.target.value, 10) || 0 }))}
              className="w-full rounded-lg border border-gray-700 bg-black/50 px-3 py-2 text-sm text-white"
            />
            <div className="flex gap-2">
              <Button size="sm" onClick={handleAddEpisode} disabled={saving || !newEpisode.title?.trim()}>
                Add episode
              </Button>
              <Button size="sm" variant="secondary" onClick={() => { setAddingEpisode(false); setNewEpisode(emptyEpisode) }}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        <div className="space-y-2">
          {episodes.map((ep) => (
            <div key={ep.episodeId} className="rounded-lg border border-gray-800 bg-black/40 overflow-hidden">
              <button
                type="button"
                className="w-full flex items-center gap-2 px-4 py-3 text-left text-sm hover:bg-white/5"
                onClick={() => (expandedEpisodeId === ep.episodeId ? setExpandedEpisodeId(null) : openEpisodeForm(ep))}
              >
                {expandedEpisodeId === ep.episodeId ? (
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-gray-400" />
                )}
                <span className="font-medium text-white">
                  Ep {ep.episodeNumber}: {ep.title || 'Untitled'}
                </span>
                {ep.duration ? (
                  <span className="text-xs text-gray-500">{Math.floor(ep.duration / 60)}m</span>
                ) : null}
                {ep.videoUrl && (
                  <a
                    href={ep.videoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ml-auto inline-flex items-center gap-1 text-xs text-[#D4AF37] hover:underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ExternalLink className="h-3 w-3" /> Open / download video
                  </a>
                )}
              </button>

              {expandedEpisodeId === ep.episodeId && episodeForms[ep.episodeId] && (
                <div className="border-t border-gray-800 p-4 space-y-3 bg-black/60">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Title</label>
                    <input
                      type="text"
                      value={episodeForms[ep.episodeId].title ?? ''}
                      onChange={(e) =>
                        setEpisodeForms((prev) => ({
                          ...prev,
                          [ep.episodeId]: { ...prev[ep.episodeId], title: e.target.value },
                        }))
                      }
                      className="w-full rounded border border-gray-700 bg-black/50 px-2 py-1.5 text-sm text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Description</label>
                    <textarea
                      value={episodeForms[ep.episodeId].description ?? ''}
                      onChange={(e) =>
                        setEpisodeForms((prev) => ({
                          ...prev,
                          [ep.episodeId]: { ...prev[ep.episodeId], description: e.target.value },
                        }))
                      }
                      className="w-full rounded border border-gray-700 bg-black/50 px-2 py-1.5 text-sm text-white"
                      rows={2}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Video URL (replace to change video)</label>
                    <input
                      type="text"
                      value={episodeForms[ep.episodeId].videoUrl ?? ''}
                      onChange={(e) =>
                        setEpisodeForms((prev) => ({
                          ...prev,
                          [ep.episodeId]: { ...prev[ep.episodeId], videoUrl: e.target.value },
                        }))
                      }
                      className="w-full rounded border border-gray-700 bg-black/50 px-2 py-1.5 text-sm text-white"
                      placeholder="https://..."
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Thumbnail URL</label>
                    <input
                      type="text"
                      value={episodeForms[ep.episodeId].thumbnailUrl ?? ''}
                      onChange={(e) =>
                        setEpisodeForms((prev) => ({
                          ...prev,
                          [ep.episodeId]: { ...prev[ep.episodeId], thumbnailUrl: e.target.value },
                        }))
                      }
                      className="w-full rounded border border-gray-700 bg-black/50 px-2 py-1.5 text-sm text-white"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-gray-500">Duration (sec)</label>
                    <input
                      type="number"
                      value={episodeForms[ep.episodeId].duration ?? 0}
                      onChange={(e) =>
                        setEpisodeForms((prev) => ({
                          ...prev,
                          [ep.episodeId]: { ...prev[ep.episodeId], duration: parseInt(e.target.value, 10) || 0 },
                        }))
                      }
                      className="w-24 rounded border border-gray-700 bg-black/50 px-2 py-1.5 text-sm text-white"
                    />
                  </div>
                  <div className="flex gap-2 pt-2">
                    <Button size="sm" onClick={() => handleSaveEpisode(ep.episodeId)} disabled={saving}>
                      Save episode
                    </Button>
                    {confirmDelete === ep.episodeId ? (
                      <>
                        <Button size="sm" variant="secondary" className="border-red-600 text-red-400" onClick={() => handleDeleteEpisode(ep.episodeId)} disabled={saving}>
                          Confirm delete
                        </Button>
                        <Button size="sm" variant="secondary" onClick={() => setConfirmDelete(null)}>Cancel</Button>
                      </>
                    ) : (
                      <Button size="sm" variant="secondary" className="border-red-800 text-red-400" onClick={() => setConfirmDelete(ep.episodeId)}>
                        <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete episode
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {episodes.length === 0 && !addingEpisode && (
          <p className="text-sm text-gray-500">No episodes yet. Add one above.</p>
        )}
      </section>
    </div>
  )
}
